/**
 * 403 denial-time merge-suggestion envelope (vingapi uptake #4216).
 *
 * When an authed request fails a membership/level check, the 403 body MAY
 * carry the user's strongest merge suggestion whose other account holds the
 * denied access:
 *
 *   { "detail": "...", "code": "insufficient_level", "merge_suggestion": {...} }
 *
 * This shape is deliberately NOT in vingapi's OpenAPI document (FastAPI does
 * not emit exception envelopes), so it cannot come from orval generation —
 * this hand-written zod schema IS the frontend's contract for it, pinned by
 * pact. Distinct from the /me `mergeSuggestion` shape ({id, verifyMethods}):
 * the denial shape additionally names the other user and the access grant.
 *
 * Parsing is fail-closed: plain `{detail}` 403s (unconverted paths), absent
 * or null merge_suggestion, and malformed envelopes all yield `null` — the
 * caller then treats the 403 as an ordinary denial.
 */

import { z } from 'zod';
import { convertKeysToCamel } from '@/lib/dataTransform';
import type { UserLevel } from '@/types';

export const DENIAL_CODES = ['no_membership', 'insufficient_level'] as const;
export type DenialCode = (typeof DENIAL_CODES)[number];

/**
 * What the other account holds that merging would make reachable. Two kinds,
 * because the two denial sites are denying different things:
 *
 * - `membership` — a seat the other account holds (the app's 403s).
 * - `content` — the org's assignment of one specific share, sitting on a
 *   contact it attaches to both accounts. The consume gate's denial is not
 *   about a level: the other account holds no seat to inherit, so the grant
 *   names the share instead.
 *
 * `kind` is accepted as optional on the membership arm so a body predating the
 * discriminator still parses; the arms are distinguishable by their fields
 * regardless.
 */
const MembershipGrantSchema = z.object({
  kind: z.literal('membership').optional(),
  contextType: z.enum(['org', 'network']),
  contextId: z.number(),
  level: z.enum(['admin', 'manager', 'viewer']),
});

const ContentGrantSchema = z.object({
  kind: z.literal('content'),
  shareStub: z.string(),
});

const DenialMergeSuggestionSchema = z.object({
  id: z.number(),
  verifyMethods: z.array(z.string()).min(1),
  otherUser: z.object({
    id: z.number(),
    displayName: z.string(),
  }),
  grants: z.union([MembershipGrantSchema, ContentGrantSchema]),
});

export interface MembershipGrant {
  kind?: 'membership';
  contextType: 'org' | 'network';
  contextId: number;
  level: UserLevel;
}

export interface ContentGrant {
  kind: 'content';
  /** The share the merge would unblock. */
  shareStub: string;
}

export type DenialGrant = MembershipGrant | ContentGrant;

/** Narrows to the content arm — the consume gate's denial. */
export function isContentGrant(grant: DenialGrant): grant is ContentGrant {
  return grant.kind === 'content';
}

export interface DenialMergeSuggestion {
  id: number;
  verifyMethods: string[];
  otherUser: { id: number; displayName: string };
  grants: DenialGrant;
}

export interface DenialEnvelope {
  suggestion: DenialMergeSuggestion;
  /** `code` is optional on the wire — plain-403 paths never send it. */
  code: DenialCode | null;
}

/**
 * Extract a merge-suggestion envelope from a raw (snake_case) 403 body.
 * Returns null unless the body carries a well-formed, non-null
 * merge_suggestion. Never throws.
 */
export function parseDenialEnvelope(rawBody: unknown): DenialEnvelope | null {
  if (rawBody === null || typeof rawBody !== 'object') return null;
  const camel = convertKeysToCamel(rawBody) as {
    code?: unknown;
    mergeSuggestion?: unknown;
  };
  if (camel.mergeSuggestion == null) return null;
  const parsed = DenialMergeSuggestionSchema.safeParse(camel.mergeSuggestion);
  if (!parsed.success) {
    // Fail closed, but loudly enough to notice contract drift in dev/tests.
    console.warn('[api/mergeDenial] 403 merge_suggestion did not match schema:', parsed.error.issues);
    return null;
  }
  const codeParsed = z.enum(DENIAL_CODES).safeParse(camel.code);
  return {
    suggestion: parsed.data,
    code: codeParsed.success ? codeParsed.data : null,
  };
}
